import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, Edit, History, Loader2, Eye } from 'lucide-react';
import { Button, utcToLocal, getTimezoneAbbr, useTimezone, showToast, Sheet, SheetContent, SheetHeader, SheetTitle } from '@t-one-internal/design-system';
import { DataGrid } from '@t-one-internal/design-system/lib/components/ui/data-grid';
import type { ColumnDef } from '@tanstack/react-table';
import CustomReportBuilder from '../../components/reports/custom-builder/CustomReportBuilder';
import ReportDataPreview from '../../components/reports/ReportDataPreview';
import ReportHistory from '../../components/reports/ReportHistory';
import Scheduler from '../../components/reports/custom-builder/components/Scheduler';
import { customReportsApi } from '../../api/customReportsApi';
import { useEmployeeProfileStore } from '../../stores/employeeProfileStore';
import type { Schedule, Recipient, ReportConfig, ReportModule, CustomReportApiResponse, ReportStatus } from '../../components/reports/custom-builder/types';

type ViewMode = 'list' | 'builder' | 'data-preview' | 'history';

interface CustomReportsPageProps {
    module?: ReportModule;
}

const CustomReportsPage: React.FC<CustomReportsPageProps> = ({ module }) => {
    const { timezone } = useTimezone();
    const currentUserUuid = useEmployeeProfileStore((s) => s.selectedEmployee?.user_uuid);
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [reports, setReports] = useState<CustomReportApiResponse[]>([]);
    const [editingReport, setEditingReport] = useState<ReportConfig | null>(null);
    const [editingReportId, setEditingReportId] = useState<string | null>(null);
    const [selectedReport, setSelectedReport] = useState<CustomReportApiResponse | null>(null);

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Edit schedule sheet
    const [editSheetOpen, setEditSheetOpen] = useState(false);
    const [editSheetReport, setEditSheetReport] = useState<CustomReportApiResponse | null>(null);
    const [editScheduleValue, setEditScheduleValue] = useState<Schedule>({ frequency: 'daily', time: '09:00', recipients: [] });
    const [originalRecipients, setOriginalRecipients] = useState<Recipient[]>([]);
    const [editSaving, setEditSaving] = useState(false);

    const loadReports = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const apiReports = await customReportsApi.getReports(module);
            setReports(apiReports);
        } catch (err) {
            console.error('Failed to load reports:', err);
            setError('Failed to load reports. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [module]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const handleSaveReport = async (config: ReportConfig) => {
        try {
            if (editingReportId) {
                await customReportsApi.updateReport(editingReportId, config);
                showToast.success('Report updated', { description: `"${config.name}" has been updated successfully.` });
            } else {
                await customReportsApi.createReport(config);
                showToast.success('Report created', { description: `"${config.name}" has been saved successfully.` });
            }
            await loadReports();
            setViewMode('list');
            setEditingReport(null);
            setEditingReportId(null);
        } catch (err) {
            console.error('Failed to save report:', err);
            showToast.error('Failed to save report', { description: 'Something went wrong. Please try again.' });
            throw err;
        }
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!confirm('Are you sure you want to delete this report?')) return;

        try {
            setActionLoading(id);
            await customReportsApi.deleteReport(id);
            await loadReports();
            showToast.success('Report deleted', { description: 'The report has been deleted successfully.' });
        } catch (err) {
            console.error('Failed to delete report:', err);
            showToast.error('Failed to delete report', { description: 'Something went wrong. Please try again.' });
        } finally {
            setActionLoading(null);
        }
    };

    const handleEdit = async (report: CustomReportApiResponse) => {
        try {
            // Fetch full report with schema to populate columns using metric_key
            const schema = await customReportsApi.fetchReportSchema(report.metric_key);
            const schemaColumns = customReportsApi.transformSchemaColumns(schema);

            // Apply saved filters/sort to columns (handle nulls)
            const columns = customReportsApi.applyApiDataToColumns(
                schemaColumns,
                report.columns || [],
                report.filters || [],
                report.order_by || []
            );

            // Default schedule if null
            const defaultSchedule = {
                frequency: 'once' as const,
                time: '09:00',
                recipients: []
            };

            // Default duration if null
            const defaultDuration = {
                type: '30d' as const,
                start_date: '',
                end_date: ''
            };

            // Transform API response to ReportConfig
            const config: ReportConfig = {
                id: report.id,
                module: (report.module as ReportModule) || module,
                name: report.name,
                description: report.description || '',
                type: report.metric_key,
                dateRange: report.time_range
                    ? customReportsApi.transformDurationToDateRange(report.time_range)
                    : { type: defaultDuration.type, startDate: '', endDate: '' },
                columns,
                schedule: report.schedule
                    ? {
                        ...customReportsApi.transformScheduleFromApi(report.schedule),
                        recipients: customReportsApi.transformRecipientsFromApi(report.recepients || [])
                    }
                    : defaultSchedule,
                status: report.status
            };

            setEditingReport(config);
            setEditingReportId(report.id);
            setViewMode('builder');
        } catch (err) {
            console.error('Failed to load report for editing:', err);
            setError('Failed to load report details.');
        }
    };

    const handleViewData = (report: CustomReportApiResponse) => {
        setSelectedReport(report);
        setViewMode('data-preview');
    };

    const handleViewHistory = (report: CustomReportApiResponse) => {
        setSelectedReport(report);
        setViewMode('history');
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedReport(null);
        setEditingReport(null);
        setEditingReportId(null);
    };

    const handleOpenEditSheet = (report: CustomReportApiResponse) => {
        const existingRecipients = customReportsApi.transformRecipientsFromApi(report.recepients || []);
        const schedule: Schedule = report.schedule
            ? {
                ...customReportsApi.transformScheduleFromApi(report.schedule),
                recipients: existingRecipients,
            }
            : { frequency: 'daily', time: '09:00', recipients: existingRecipients };

        setEditSheetReport(report);
        setEditScheduleValue(schedule);
        setOriginalRecipients(existingRecipients);
        setEditSheetOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editSheetReport) return;
        setEditSaving(true);
        try {
            // Compute recipient diffs
            const added = editScheduleValue.recipients.filter(
                (r) => !originalRecipients.some((o) => o.email === r.email && o.isExternal === r.isExternal && o.userId === r.userId)
            );
            const removed = originalRecipients.filter(
                (o) => !editScheduleValue.recipients.some((r) => r.email === o.email && r.isExternal === o.isExternal && r.userId === o.userId)
            );

            // Send schedule update + recipient changes in one call
            await customReportsApi.updateReportSchedule(editSheetReport.id, editScheduleValue, added, removed);

            await loadReports();
            setEditSheetOpen(false);
            showToast.success('Schedule updated', { description: 'Report schedule and recipients have been updated.' });
        } catch (err) {
            console.error('Failed to save schedule:', err);
            showToast.error('Failed to save', { description: 'Something went wrong. Please try again.' });
        } finally {
            setEditSaving(false);
        }
    };

    const handleToggleStatus = async (report: CustomReportApiResponse) => {
        const newStatus: ReportStatus = report.status === 'active' ? 'paused' : 'active';
        try {
            setActionLoading(report.id);
            await customReportsApi.updateReportStatus(report.id, newStatus);
            await loadReports();
        } catch (err) {
            console.error('Failed to update status:', err);
            setError('Failed to update report status.');
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadgeClass = (status: ReportStatus) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'paused':
                return 'bg-yellow-100 text-yellow-800';
            case 'draft':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const columns = useMemo<ColumnDef<CustomReportApiResponse>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Report Name',
            size: 300,
            cell: ({ row }) => {
                const report = row.original;
                const isShared = currentUserUuid && report.created_by !== currentUserUuid;
                return (
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{report.name}</span>
                            {isShared && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
                                    Shared by {report.created_by_name || 'Unknown'}
                                </span>
                            )}
                        </div>
                        {report.description && <div className="text-xs text-gray-500 truncate max-w-[260px]">{report.description}</div>}
                    </div>
                );
            }
        },
        {
            accessorKey: 'report_type_name',
            header: 'Report Type',
            size: 180,
            cell: ({ getValue }) => (
                <span className="text-sm text-gray-700">{getValue() as string}</span>
            )
        },
        {
            accessorKey: 'schedule',
            header: 'Frequency',
            size: 130,
            cell: ({ row }) => {
                const schedule = row.original.schedule;
                if (!schedule) return <span className="text-gray-400">—</span>;
                const localTime = schedule.time ? utcToLocal(schedule.time, timezone) : schedule.time;
                const tzAbbr = getTimezoneAbbr(timezone);
                return (
                    <div>
                        <div className="text-sm capitalize font-medium">{schedule.frequency}</div>
                        <div className="text-xs text-gray-500">{localTime} {tzAbbr}</div>
                    </div>
                );
            }
        },
        {
            accessorKey: 'next_run_at',
            header: 'Next Run',
            size: 140,
            cell: ({ getValue }) => {
                const val = getValue() as string | null;
                if (!val) return <span className="text-gray-400">—</span>;
                const date = new Date(val);
                return (
                    <div>
                        <div className="text-sm text-gray-900">{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                        <div className="text-xs text-gray-500">{date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                );
            }
        },
        {
            accessorKey: 'recepients',
            header: 'Recipients',
            size: 100,
            enableSorting: false,
            cell: ({ row }) => {
                const recipients = row.original.recepients || [];
                return (
                    <span className="text-sm text-gray-700">{recipients.length} {recipients.length === 1 ? 'person' : 'people'}</span>
                );
            }
        },
        {
            accessorKey: 'status',
            header: 'Status',
            size: 100,
            cell: ({ row }) => (
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getStatusBadgeClass(row.original.status)}`}>
                    {row.original.status}
                </span>
            )
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            enableColumnFilter: false,
            enableSorting: false,
            size: 160,
            cell: ({ row }) => {
                const report = row.original;
                return (
                    <div className="flex items-center justify-start gap-2">
                        {/* View Data */}
                        <button
                            onClick={() => handleViewData(report)}
                            className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 transition-colors"
                            title="View Live Data"
                        >
                            <Eye size={16} />
                        </button>

                        {/* View History */}
                        <button
                            onClick={() => handleViewHistory(report)}
                            className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
                            title="View History"
                        >
                            <History size={16} />
                        </button>

                        {/* Edit Schedule & Recipients */}
                        <button
                            onClick={() => handleOpenEditSheet(report)}
                            className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit Schedule"
                        >
                            <Edit size={16} />
                        </button>

                        {/* Delete */}
                        <button
                            onClick={(e) => handleDelete(report.id, e)}
                            disabled={actionLoading === report.id}
                            className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Delete"
                        >
                            {actionLoading === report.id ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Trash2 size={16} />
                            )}
                        </button>
                    </div>
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [actionLoading, timezone, currentUserUuid]);

    // Render based on viewMode
    if (viewMode === 'builder') {
        return (
            <CustomReportBuilder
                module={module}
                initialConfig={editingReport || undefined}
                onCancel={handleBackToList}
                onSave={handleSaveReport}
            />
        );
    }

    if (viewMode === 'data-preview' && selectedReport) {
        return (
            <ReportDataPreview
                report={selectedReport}
                onBack={handleBackToList}
            />
        );
    }

    if (viewMode === 'history' && selectedReport) {
        return (
            <ReportHistory
                report={selectedReport}
                onBack={handleBackToList}
            />
        );
    }

    // Default: list view
    return (
        <div className="flex flex-col h-full">
            {error && (
                <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between items-center">
                    <span>{error}</span>
                    <button
                        onClick={() => setError(null)}
                        className="ml-2 underline hover:no-underline font-medium"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            <div className="flex-1 overflow-hidden px-6 pb-6 pt-6">
                <DataGrid
                    data={reports}
                    columns={columns}
                    loading={isLoading}
                    showToolbar={true}
                    searchColumn="name"
                    searchPlaceholder="Search scheduled reports..."
                    enableRowSelection={false}
                    enableSorting={true}
                    showPagination={true}
                    defaultPageSize={10}
                    rowDensity="normal"
                    fullHeight={true}
                />
            </div>

            {/* Edit Schedule Sheet */}
            <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
                <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
                    <SheetHeader className="pb-4 border-b border-gray-200">
                        <SheetTitle className="text-lg font-semibold">Edit Schedule</SheetTitle>
                        <p className="text-sm text-gray-500 mt-1">{editSheetReport?.name}</p>
                    </SheetHeader>

                    <div className="py-6 space-y-6">
                        {/* Status Toggle */}
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Status</label>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadgeClass(editSheetReport?.status || 'active')}`}>
                                    {editSheetReport?.status}
                                </span>
                                {editSheetReport && editSheetReport.schedule?.frequency !== 'once' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleToggleStatus(editSheetReport)}
                                        disabled={actionLoading === editSheetReport.id}
                                    >
                                        {actionLoading === editSheetReport.id ? 'Updating...' : editSheetReport.status === 'active' ? 'Pause' : 'Resume'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Scheduler (frequency, time, recipients) */}
                        <Scheduler value={editScheduleValue} onChange={setEditScheduleValue} />
                    </div>

                    <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                        <Button
                            className="flex-1"
                            onClick={handleSaveEdit}
                            disabled={editSaving}
                        >
                            {editSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button variant="outline" onClick={() => setEditSheetOpen(false)}>
                            Cancel
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default CustomReportsPage;
